import { Button, Card, CardContent, FormField } from '@/components/ui';
import { useAppDispatch, useAppSelector } from '@/config/store';
import { Form } from '@/shared/form';
import { useEffect, useState } from 'react';
import { Translate, translate } from 'react-jhipster';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { PasswordResetState, handlePasswordResetFinish, reset } from '../password-reset.reducer';

const schema = z
  .object({
    newPassword: z
      .string()
      .min(1, 'This field is required.')
      .min(4, 'Your password is required to be at least 4 characters.')
      .max(50, 'Your password cannot be longer than 50 characters.'),
    confirmPassword: z.string().min(1, 'This field is required.'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "The password and its confirmation do not match!",
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

const PasswordResetFinishPage = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [token] = useState<string>(searchParams.get('token') || '');
  const [userId] = useState<string>(searchParams.get('userId') || '');
  const { resetPasswordSuccess } = useAppSelector<PasswordResetState>((state) => state.passwordReset);

  useEffect(
    () => () => {
      dispatch(reset());
    },
    [dispatch]
  );

  const handleValidSubmit = (data: FormValues) => {
    dispatch(handlePasswordResetFinish({ userId, token, newPassword: data.newPassword }));
  };

  const missingTokenMessageBox = () => (
    <p className="mb-2 text-[var(--color-mockup-text-mid)]">
      <Translate contentKey="reset.finish.messages.keymissing" />
    </p>
  );

  const successMessageBox = () => (
    <p className="mb-2 text-[var(--color-mockup-text-mid)]">
      <Translate contentKey="reset.finish.messages.success" />
    </p>
  );

  const passwordForm = () => (
    <>
      <p className="mb-2 text-[var(--color-mockup-text-mid)]">
        <Translate contentKey="reset.finish.messages.info" />
      </p>
      <Form schema={schema} onSubmit={handleValidSubmit} defaultValues={{ newPassword: '', confirmPassword: '' }}>
        <div className="space-y-4">
          <FormField
            name="newPassword"
            label={translate('global.form.newpassword.label')}
            placeholder={translate('global.form.newpassword.placeholder')}
            type="password"
            autoComplete="new-password"
          />
          <FormField
            name="confirmPassword"
            label={translate('global.form.confirmpassword.label')}
            placeholder={translate('global.form.confirmpassword.placeholder')}
            type="password"
            autoComplete="new-password"
          />
          <Button type="submit" className="my-2 w-full">
            <Translate contentKey="reset.finish.form.button" />
          </Button>
        </div>
      </Form>
    </>
  );

  const successMessage = useAppSelector((state) => state.passwordReset.successMessage);
  useEffect(() => {
    if (successMessage) toast.success(successMessage);
  }, [successMessage]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="max-w-[800px] overflow-hidden text-[var(--color-mockup-text-mid)]">
        <div className="grid grid-rows-[min-content_auto_min-content]">
          <header className="border-b border-[var(--color-mockup-border)]">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4">
              <h1 className="text-xl font-medium text-[var(--color-mockup-text)]">
                <Translate contentKey="reset.finish.title" />
              </h1>
            </div>
          </header>
          <CardContent className="p-5">
            {!token ? missingTokenMessageBox() : resetPasswordSuccess ? successMessageBox() : passwordForm()}
            <div className="mt-6 text-center">
              <p className="text-sm text-[var(--color-mockup-text-light)]">
                <Link to="/login" className="text-primary hover:underline">
                  Torna alla pagina principale
                </Link>
              </p>
            </div>
          </CardContent>
        </div>
      </Card>
    </div>
  );
};

export default PasswordResetFinishPage;
